<template>
  <v-container fluid class="signup-page pa-4 pa-sm-6">
    <v-row justify="center">
      <v-col cols="12" lg="10" xl="9">
        <v-card class="signup-shell" rounded="xl" elevation="14">
          <v-row no-gutters>
            <v-col cols="12" md="4" class="signup-hero px-4 pb-3">
              <div class="signup-hero__logo">
                <v-img :src="logo" class="mx-auto" height="92" width="210" contain></v-img>
              </div>

              <div class="signup-hero__copy">
                <p class="text-overline signup-hero__eyebrow">Newham Community Project</p>
                <h1 class="signup-hero__title">Member registration</h1>
                <p class="signup-hero__subtitle">
                  Complete the form step by step. Your progress stays visible while you work through the registration.
                </p>
              </div>

              <div class="signup-hero__steps">
                <div
                  v-for="item in visibleSteps"
                  :key="item.value"
                  class="step-pill"
                  :class="{
                    'step-pill--active': step === item.value,
                    'step-pill--done': step > item.value
                  }"
                >
                  <span class="step-pill__number">{{ item.value }}</span>
                  <div>
                    <div class="step-pill__title">{{ item.title }}</div>
                    <div class="step-pill__subtitle">{{ item.subtitle }}</div>
                  </div>
                </div>
              </div>

              <v-card class="signup-hero__note" rounded="xl" variant="tonal">
                <v-card-text>
                  <div class="signup-hero__note-title">Helpful tip</div>
                  <p class="mb-0">
                    Start with the postcode field, then select the matching address and building number to auto-fill the borough.
                  </p>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="8" class="signup-form-column">
              <div class="signup-form">
                <div class="signup-form__header">
                  <div>
                    <div class="step-badge">Step {{ visibleSteps.findIndex(item => item.value === step) + 1 }} of {{ visibleStepCount }}</div>
                    <h2 class="signup-form__title">{{ currentStepMeta.title }}</h2>
                    <p class="signup-form__subtitle">{{ currentStepMeta.subtitle }}</p>
                  </div>
                  <div class="signup-form__progress">
                    <span>{{ Math.round(stepProgress) }}%</span>
                    <v-progress-linear
                      :model-value="stepProgress"
                      color="#2f6f65"
                      bg-color="#dfe8e5"
                      rounded
                      height="8"
                    ></v-progress-linear>
                  </div>
                </div>

                <v-alert
                  v-if="grp_err_text"
                  type="warning"
                  variant="tonal"
                  class="mb-6"
                  rounded="lg"
                >
                  {{ grp_err_text }}
                </v-alert>

                <v-window v-model="step" :touch="false">
                  <v-window-item :value="1">
                    <div class="section-block">
                      <div class="section-block__header">
                        <h3>Personal information</h3>
                        <p>Add your main contact and household details.</p>
                      </div>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-autocomplete
                            v-model="titles"
                            :items="Title"
                            label="Title"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            :error-messages="errorMessage('titles')"
                            :menu-props="{ maxHeight: 300 }"
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="firstName"
                            label="First Name"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            :error-messages="errorMessage('firstName')"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="secondName"
                            label="Last Name"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="phone"
                            label="Phone"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            :error-messages="errorMessage('phone')"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="email"
                            label="Email"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            :error-messages="errorMessage('email')"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-autocomplete
                            v-model="type"
                            :items="Role"
                            item-title="type"
                            item-value="id"
                            label="Member Type"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            clearable
                            :error-messages="errorMessage('type')"
                          ></v-autocomplete>
                        </v-col>
                      </v-row>
                    </div>

                    <v-sheet
                      v-if="type == 3"
                      class="section-card mt-6"
                      color="#f7fbfa"
                      rounded="xl"
                      border
                    >
                      <div class="section-block__header">
                        <h3>Dependent verification</h3>
                        <p>Link this registration to the already registered member.</p>
                      </div>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="DepEmail"
                            label="Registered Member Email"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            :error-messages="errorMessage('DepEmail')"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="DepPhone"
                            label="Registered Member Phone"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            :error-messages="errorMessage('DepPhone')"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <div class="section-divider">or use the registered member ID</div>
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            v-model="DepId"
                            label="Registered Member ID"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            :error-messages="errorMessage('DepId')"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-sheet>

                    <div v-if="type != 3">
                      <v-sheet class="section-card mt-6" color="#f7fbfa" rounded="xl" border>
                        <div class="section-block__header">
                          <h3>Household details</h3>
                          <p>Tell us about dependents and children living with you in the UK.</p>
                        </div>
                        <v-row>
                          <v-col cols="12">
                            <v-autocomplete
                              v-model="heveDependent"
                              :items="isDependent"
                              item-title="title"
                              item-value="val"
                              label="Do you have dependents living in the UK?"
                              variant="outlined"
                              density="comfortable"
                              rounded="lg"
                              hide-details="auto"
                              clearable
                            ></v-autocomplete>
                          </v-col>
                          <v-col cols="12">
                            <v-autocomplete
                              v-model="NoKids"
                              :items="CHnumber"
                              label="Number of kids living with you in the UK"
                              variant="outlined"
                              density="comfortable"
                              rounded="lg"
                              hide-details="auto"
                              clearable
                              :error-messages="errorMessage('NoKids')"
                            ></v-autocomplete>
                          </v-col>
                        </v-row>

                        <v-expand-transition>
                          <div v-if="heveDependent" class="mt-2">
                            <v-row>
                              <v-col cols="12" md="4">
                                <v-text-field
                                  v-model="dependentName"
                                  label="Dependent Name"
                                  variant="outlined"
                                  density="comfortable"
                                  rounded="lg"
                                  hide-details="auto"
                                  :error-messages="errorMessage('dependentName')"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="4">
                                <v-text-field
                                  v-model="dependentPhone"
                                  label="Dependent Phone"
                                  variant="outlined"
                                  density="comfortable"
                                  rounded="lg"
                                  hide-details="auto"
                                  :error-messages="errorMessage('dependentPhone')"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="4">
                                <v-text-field
                                  v-model="dependentemail"
                                  label="Dependent Email"
                                  variant="outlined"
                                  density="comfortable"
                                  rounded="lg"
                                  hide-details="auto"
                                  :error-messages="errorMessage('dependentemail')"
                                ></v-text-field>
                              </v-col>
                            </v-row>
                          </div>
                        </v-expand-transition>
                      </v-sheet>
                    </div>

                    <v-expand-transition>
                      <v-sheet
                        v-if="NoKids == 'Expected'"
                        class="section-card mt-6"
                        color="#fffaf2"
                        rounded="xl"
                        border
                      >
                        <div class="section-block__header">
                          <h3>Expected child details</h3>
                          <p>Add the expected date.</p>
                        </div>
                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="ExpectedDate"
                              type="date"
                              label="Expected Date (Approx)"
                              variant="outlined"
                              density="comfortable"
                              rounded="lg"
                              hide-details="auto"
                              :error-messages="errorMessage('ExpectedDate')"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-sheet>
                    </v-expand-transition>
                  </v-window-item>

                  <v-window-item :value="2">
                    <div class="section-block">
                      <div class="section-block__header">
                        <h3>Address details</h3>
                        <p>Select a postcode first, then choose the address and building number.</p>
                      </div>
                      <v-row>
                        <v-col cols="12">
                          <v-autocomplete
                            v-model="zib"
                            v-model:search="search"
                            :items="postCodeList"
                            label="Post Code"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            clearable
                            :error-messages="errorMessage('zib')"
                          >
                            <template v-slot:no-data>
                              <v-list-item>
                                <v-list-item-title>
                                  Search for <strong>Post Code</strong>
                                </v-list-item-title>
                              </v-list-item>
                            </template>
                          </v-autocomplete>
                        </v-col>
                        <v-col cols="12">
                          <v-autocomplete
                            v-model="address"
                            :items="addressList"
                            item-title="line_1"
                            label="Address"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            clearable
                            :error-messages="errorMessage('address')"
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-autocomplete
                            v-model="country"
                            :items="buildingList"
                            label="Building No"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            clearable
                            :error-messages="errorMessage('country')"
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="borough"
                            label="Borough"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            readonly
                            :error-messages="errorMessage('borough')"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </div>
                  </v-window-item>

                  <v-window-item :value="3">
                    <div class="section-block">
                      <div class="section-block__header">
                        <h3>Education details</h3>
                        <p>Complete the student and course information.</p>
                      </div>
                      <v-row>
                        <v-col cols="12">
                          <v-text-field
                            v-model="studentId"
                            label="Student ID"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            :error-messages="errorMessage('studentId')"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-autocomplete
                            v-model="university"
                            v-model:search="searchUnsty"
                            :items="universityLis"
                            item-title="name"
                            item-value="id"
                            label="University"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            clearable
                            :error-messages="errorMessage('university')"
                          >
                            <template v-slot:no-data>
                              <v-list-item>
                                <v-list-item-title>
                                  Search for <strong>University</strong>
                                </v-list-item-title>
                              </v-list-item>
                            </template>
                          </v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="ArrivedDate"
                            type="date"
                            label="Arrived Date"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            :error-messages="errorMessage('ArrivedDate')"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="startDate"
                            type="date"
                            label="Course Start Date"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            :error-messages="errorMessage('startDate')"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="EndDate"
                            type="date"
                            :min="minEndDate"
                            label="Course End Date"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            :error-messages="errorMessage('EndDate')"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </div>
                  </v-window-item>

                  <v-window-item :value="4">
                    <div class="section-block">
                      <div class="section-block__header">
                        <h3>Collection Preferences</h3>
                        <p>Let us know your dietary preferences and preferred collection day.</p>
                      </div>
                      <v-row>
                        <v-col cols="12">
                          <v-autocomplete
                            v-model="deitryRequirment"
                            :items="dietReq"
                            item-title="name"
                            item-value="id"
                            label="Dietary Requirements"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            clearable
                            :error-messages="errorMessage('deitryRequirment')"
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12">
                          <v-autocomplete
                            v-model="eggs"
                            :items="eggList"
                            item-title="name"
                            item-value="id"
                            label="Do you eat Eggs?"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            clearable
                            :error-messages="errorMessage('eggs')"
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12">
                          <v-autocomplete
                            v-model="collectionDay"
                            :items="prefCollection"
                            label="Preferred Collection Day"
                            variant="outlined"
                            density="comfortable"
                            rounded="lg"
                            hide-details="auto"
                            clearable
                            :error-messages="errorMessage('collectionDay')"
                          ></v-autocomplete>
                        </v-col>
                        <v-col cols="12">
                          <v-sheet class="terms-card" rounded="xl" color="#f7fbfa" border>
                            <v-checkbox
                              v-model="select"
                              color="#2f6f65"
                              hide-details
                            >
                              <template #label>
                                <div class="terms-card__label">
                                  I agree that this data will be stored and processed for the purpose of establishing contact, and I understand that I can revoke my consent at any time.
                                </div>
                              </template>
                            </v-checkbox>
                          </v-sheet>
                        </v-col>
                      </v-row>
                    </div>
                  </v-window-item>

                  <v-window-item :value="5">
                    <div class="section-block" v-if="showKidsStep">
                      <div class="section-block__header">
                        <h3>Kid's details</h3>
                        <p>Add each child's name and date of birth.</p>
                      </div>
                      <v-row>
                        <v-col cols="12" v-for="hh in parseInt(this.NoKids)" :key="hh">
                          <v-card class="child-card" rounded="xl" variant="outlined">
                            <v-card-text>
                              <div class="child-card__title">Child {{ hh }}</div>
                              <v-row>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    v-model="child[hh - 1].name"
                                    :label="'Child Name ' + hh"
                                    variant="outlined"
                                    density="comfortable"
                                    rounded="lg"
                                    hide-details="auto"
                                  ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-text-field
                                    v-model="child[hh - 1].dob"
                                    type="date"
                                    label="Date of Birth"
                                    variant="outlined"
                                    density="comfortable"
                                    rounded="lg"
                                    hide-details="auto"
                                  ></v-text-field>
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </v-col>
                        <v-col cols="12" v-if="errValue">
                          <v-alert type="error" variant="tonal" rounded="lg">
                            {{ errValue }}
                          </v-alert>
                        </v-col>
                      </v-row>
                    </div>
                  </v-window-item>
                </v-window>

                <div class="signup-actions">
                  <v-btn
                    v-if="step > 1"
                    @click="goBack()"
                    variant="outlined"
                    color="#2f6f65"
                    rounded="lg"
                    size="large"
                  >
                    Back
                  </v-btn>
                  <div class="signup-actions__spacer"></div>
                  <v-btn
                    v-if="step < 4"
                    @click="nextwindow()"
                    color="#2f6f65"
                    rounded="lg"
                    size="large"
                    elevation="0"
                  >
                    {{ nextbtnName }}
                  </v-btn>
                  <v-btn
                    v-if="step == 4"
                    @click="submitMemeber()"
                    color="#2f6f65"
                    rounded="lg"
                    size="large"
                    elevation="0"
                  >
                    Submit
                  </v-btn>
                  <v-btn
                    v-if="step == 5"
                    @click="nextwindow33()"
                    color="#2f6f65"
                    rounded="lg"
                    size="large"
                    elevation="0"
                  >
                    Next
                  </v-btn>
                </div>
              </div>

              <snackbarInitail :visible="snackbarIn" @close="snackbarIn = false" />
              <snackbarVue :visible="snackbar" :text="mssg" :button="false" :color="snackColor" @close="snackbar = false" />
              <dialogVue :visible="dialogBox" @close="dialogBox = false" @yes="dialogBox = false, step++" @no="noGroup" />
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions } from 'vuex'
import useVuelidate from "@vuelidate/core";
import { helpers, required, numeric, maxLength, email } from "@vuelidate/validators";
import snackbarVue from '@/components/snackbar';
import snackbarInitail from '@/components/snackbarInitail';
import dialogVue from '@/components/dialog.vue';

import axios from 'axios'

const isAfterToday = (value) => {
  if (!value) return true

  const selectedDate = new Date(`${value}T00:00:00`)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return selectedDate > today
}

const endDateAfterToday = helpers.withMessage(
  'Course End Date must be greater than today',
  isAfterToday
)

export default {
  name: 'addmemeberVue',
  components: {
    snackbarVue,
    snackbarInitail,
    dialogVue
  },

  data() {
    return {
      v$: useVuelidate(),
      errValue: '',
      snackbarIn: true,
      snackbar: false,
      dialogBox: false,
      mssg: '',
      grp_err_text: null,
      snackColor: 'green',
      nextbtnName: 'Next',
      select: '',
      logo: require('@/assets/newham.jpeg'),
      step: 1,
      formSteps: [
        { value: 1, title: 'Personal details', subtitle: 'Basic contact and household information' },
        { value: 2, title: 'Contact details', subtitle: 'Postcode, address, and borough details' },
        { value: 3, title: 'Education details', subtitle: 'Student and university information' },
        { value: 4, title: 'Collection Preferences', subtitle: 'Dietary preferences and collection day' },
        { value: 5, title: "Kid's details", subtitle: 'Names and dates of birth for children' }
      ],
      titles: '',
      firstName: '',
      secondName: '',
      phone: '',
      email: '',
      type: '',
      // DependentName: null,
      NoKids: null,
      ExpectedDate: '',
      area: '',
      country: '',
      borough: '',
      address: '',
      zib: '',
      studentId: '',
      university: '',
      dialog: false,
      // ArrivedDate: new Date().toISOString().substr(0, 10),
      // startDate: new Date().toISOString().substr(0, 10),
      // EndDate: new Date().toISOString().substr(0, 10),

      DepEmail: '',
      DepPhone: '',
      DepId: '',

      ArrivedDate: '',
      startDate: '',
      EndDate: '',
      deitryRequirment: '',
      eggs: '',
      collectionDay: '',
      Title: ['Mr', 'Mrs', 'Miss', 'Ms',],
      CHnumber: ['None', '1', '2', '3', '4', '5', 'Expected'],
      areas: [
        {
          id: 0,
          name: 'city',
        },
        {
          id: 1,
          name: 'Town',
        }
      ],
      dietReq: [
        {
          id: 1,
          name: 'Halal'
        },
        {
          id: 2,
          name: 'Non-Halal'
        },
        {
          id: 3,
          name: 'Vegiterian'
        },
      ],
      eggList: [
        {
          id: 1,
          name: 'Yes'
        },
        {
          id: 0,
          name: 'No'
        },
      ],
      Role: [
        {
          id: 1,
          type: 'Student'
        },
        {
          id: 3,
          type: 'Dependent'
        },
        // {
        //   id: 3,
        //   type: 'Student with Children'
        // },
        {
          id: 4,
          type: 'PSW'
        },
        {
          id: 5,
          type: 'TIER2'
        },
        {
          id: 6,
          type: 'Undocumented'
        },
        {
          id: 7,
          type: 'Benefits'
        },
      ],
      Borough: [{ id: 1, name: '' }],
      univer: [{ id: 1, name: '' }],
      child: [],
      heveDependent: null,
      isDependent: [
        {
          val: true,
          title: 'Yes'
        },
        {
          val: false,
          title: 'No'
        }
      ],
      dependentName: null,
      dependentPhone: '',
      dependentemail: '',
      prefCollection: ['TUESDAY AFTERNOON (STRICTLY Ladies with Children ONLY', 'FRIDAY AFTERNOON (STRICTLY Ladies with Children ONLY)'],
      search: '',
      searchUnsty: '',
      buildingList: [],
      formAddress: [],
      memberType: ''


    }
  },

  validations() {
    return {
      titles: { required },
      firstName: { required },
      // secondName: { required },
      phone: { required, numeric, maxLength: maxLength(12) },
      email: { required, email },
      type: { required },
      zib: { required },
      // DependentName: { required },
      NoKids: { required },
      ExpectedDate: { required },
      // area: { required },
      country: { required },
      borough: { required },
      address: { required },


      studentId: { required },
      university: { required },
      ArrivedDate: { required },
      startDate: { required },
      EndDate: { required, endDateAfterToday },

      deitryRequirment: { required },
      eggs: { required },
      collectionDay: { required },

      DepEmail: { required, email },
      DepPhone: { required, numeric, maxLength: maxLength(12) },
      DepId: { required },

      dependentemail: { required, email },
      dependentPhone: { required, numeric, maxLength: maxLength(12) },
      dependentName: { required },

      // child: {
      //   $each: helpers.forEach({
      //     name: {
      //       require
      //     },
      //     dob: {
      //       require
      //     }
      //   })
      // }

    }
  },

  watch: {
    type(newVl) {
      if (newVl == 3) {
        this.nextbtnName = 'submit'
      } else {
        this.nextbtnName = 'next'

      }
      if (newVl) {
        const memType = this.Role.find(o => o.id == newVl)
        this.memberType = memType.type
      }
    },
    search(val) {
      this.GET_POST_CODE_LIST(val);

    },
    searchUnsty(val) {
      if (val) {
        this.GET_UNIVERSITY_LIST({ name: val, size: 6 }).then(() => {
          this.univer = this.universityLis;
        })
      }

      // console.log(val)
    },
    async zib(val) {
      this.address = ''
      await this.GET_ADDRESS_LSIT(val)
      this.borough = this.boroughFromAddress
      // console.log(val)
    },
    address(val) {
      const add = this.$store.getters["form/getAddresByline_1"](val);
      // console.log(add)
      this.formAddress = add[0]
      // console.log(this.formAddress)
      this.buildingList = [];
      this.country = ''
      add.forEach(el => {
        this.buildingList.push(el.building_number)
        // console.log(this.buildingList)
      })
    },
    step(val) {
      if (val !== 2) {
        this.grp_err_text = null
      }
    },
    NoKids() {
      // this.ExpectedDate = ''
      if (this.val != 'Expected') {
        this.ExpectedDate = ''
      }
    }



  },
  computed: {
    ...mapState('member', ['memberRole', 'memberEx', 'duplicateCheck']),
    ...mapState('borough', ['boroughList']),
    ...mapState('university', ['universityLis']),
    ...mapState('form', ['postCodeList', 'addressList', 'boroughFromAddress']),
    currentStepMeta() {
      return this.formSteps.find(item => item.value === this.step) || this.formSteps[0]
    },
    visibleSteps() {
      return this.formSteps.filter(item => item.value !== 5 || this.showKidsStep || this.step === 5)
    },
    visibleStepCount() {
      return this.visibleSteps.length
    },
    minEndDate() {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow.toISOString().split('T')[0]
    },
    stepProgress() {
      const currentIndex = this.visibleSteps.findIndex(item => item.value === this.step)
      return ((currentIndex + 1) / this.visibleStepCount) * 100
    },
    showKidsStep() {
      return this.NoKids && !isNaN(this.NoKids)
    }

    // computed: {
    //   currentTitle() {
    //     switch (this.step) {
    //       case 1: return 'Sign-up'
    //       case 2: return 'Create a password'
    //       default: return 'Account created'
    //     }
    //   },
    // },
    // show: {
    //   get() {
    //     return this.visible
    //   },
    //   set(value) {
    //     if (!value) {
    //       this.$emit('close')
    //     }
    //   }
    // }
  },
  methods: {
    ...mapActions('member', ['GET_MEMBER_LIST', 'GET_MEMBER_ROLE', 'ADD_MEMBER', 'GET_MEMBER_BY_ID', 'UPDATE_MEMBER', 'CHECK_MEMBER_EXIST', 'UPDATE_DEPENTENT', 'CHECK_DUPLICATE_PHONE_EMAIL']),
    ...mapActions('borough', ['GET_BORUGH_LIST']),
    ...mapActions('university', ['GET_UNIVERSITY_LIST']),
    ...mapActions('form', ['GET_POST_CODE_LIST', 'GET_ADDRESS_LSIT']),
    errorMessage(field) {
      const control = this.v$[field]
      return control && control.$error ? [control.$errors[0].$message] : []
    },
    // test() {
    //   this.mssg = 'Please Add Registered Student Details';
    //   this.snackColor = 'error';
    //   return this.snackbar = true
    // },
    nextwindow33() {
      // console.log(this.child);
      // this.child.forEach(el =>{
      //   console.log(el.name, el.dob)
      // })
      for (let i = 0; i < this.child.length; i++) {
        // console.log(this.child[i])
        if (this.child[i].name == '' || this.child[i].dob == '') {
          // console.log('please Add All values')
          return this.errValue = 'please Add All values';
        }
      }
      this.errValue = ''
      this.step = 2
      this.Borough = this.boroughList;
      // this.univer = this.universityLis;
      // this.v$.child.$validate();
      // console.log( this.v$)

    },
    async nextwindow() {
      // console.log(this.v$)
      if (this.step == 1) {
        this.v$.$reset();
        this.v$.titles.$validate();
        this.v$.firstName.$validate();
        this.v$.phone.$validate();
        this.v$.email.$validate();
        this.v$.type.$validate();
        //  this.v$.$validate();
        if (this.type == 3) {
          // this.v$.DependentName.$validate();
          if (!this.DepEmail && !this.DepPhone && !this.DepId) {
            this.mssg = 'Please Add Registered Student Details';
            this.snackColor = 'error';
            return this.snackbar = true
          }
          if (this.DepEmail || this.DepPhone) {
            this.v$.DepEmail.$validate();
            this.v$.DepPhone.$validate();
          }
          else {
            this.v$.DepId.$validate();

          }
        }
        if (this.NoKids == 'Expected') {
          this.v$.ExpectedDate.$validate();
        }
        if (this.heveDependent) {
          this.v$.dependentemail.$validate();
          this.v$.dependentPhone.$validate();
          this.v$.dependentName.$validate();

        }


        // console.log(this.v$)
        // this.v$.$validate()
        //  console.log(this.v$.$errors)
        if (this.v$.$errors.length == 0) {
          if (this.type == 3) {
            const query = {
              phne: this.DepPhone,
              id: this.DepId,
              email: this.DepEmail
            }
            await this.CHECK_MEMBER_EXIST(query).then(() => {
              console.log(this.memberEx)
              if (this.memberEx == null) {
                this.snackColor = 'error';
                this.mssg = 'No Student found'
                this.snackbar = true;
              } else if (this.memberEx.dependentemail || this.memberEx.dependentphone) {
                this.snackColor = 'error';
                this.mssg = 'Student already has an Dependent'
                this.snackbar = true;
              } else {
                const payload = {
                  id: this.memberEx.id,
                  dependentname: this.firstName + ' ' + this.secondName,
                  dependentphone: this.phone,
                  dependentemail: this.email
                }
                console.log(payload)
                this.UPDATE_DEPENTENT(payload).then(() => {
                  this.titles = ''
                  this.firstName = ''
                  this.secondName = ''
                  this.phone = ''
                  this.email = ''
                  this.DepPhone = ''
                  this.DepEmail = ''
                  this.DepId = ''
                  this.snackColor = 'success';
                  this.mssg = 'Registration completed successfully'
                  this.snackbar = true;
                  this.v$.$reset();
                })

              }
            }).catch(err => {
              console.log(err)
            })

          }
          else if (this.NoKids && !isNaN(this.NoKids)) {
            // console.log(isNaN(this.NoKids))
            this.v$.NoKids.$validate();
            if (this.v$.$errors.length == 0) {
              const checkDet = {
                phone: this.phone,
                email: this.email
              }
              this.CHECK_DUPLICATE_PHONE_EMAIL(checkDet).then(() => {
                if (this.duplicateCheck.response == 'failed') {
                  this.mssg = this.duplicateCheck.message;
                  this.snackColor = 'error';
                  return this.snackbar = true
                } else {
                  this.child = [];
                  for (let i = 0; i < parseInt(this.NoKids); i++) {
                    const obj = {
                      name: '',
                      dob: ''
                    }
                    this.child.push(obj);

                  }
                  // console.log(this.child)
                  this.step = 5
                }
              })



            }

          }
          else {
            const checkDet = {
              phone: this.phone,
              email: this.email
            }
            // console.log(checkDet)

            this.CHECK_DUPLICATE_PHONE_EMAIL(checkDet).then(() => {
              if (this.duplicateCheck.response == 'failed') {
                this.mssg = this.duplicateCheck.message;
                this.snackColor = 'error';
                return this.snackbar = true

              } else {
                this.step++

                this.Borough = this.boroughList;
                this.univer = this.universityLis;
              }
            })

          }

        }


      } else if (this.step == 2) {
        debugger
        // this.v$.area.$validate();
        // this.v$.country.$validate();
        // this.v$.borough.$validate();
        this.v$.address.$validate();
        this.v$.zib.$validate();

        if (this.v$.$errors.length == 0) {
          if (this.type != 1) {
            this.studentId = null;
            this.university = null;
            this.ArrivedDate = null;
            this.startDate = null;
            this.EndDate = null;
            return this.step = 4
          }
          // check if the address is already in the database
          // if (this.heveDependent || this.NoKids && !isNaN(this.NoKids) || this.ExpectedDate) return this.step++
          const payload = {
            country: this.country,
            road: this.address,
            zip: this.zib
          }
          console.log(payload)
          const groupCheck = await axios({
            method: 'get',
            url: `${process.env.VUE_APP_BASE_URL}members/checksame_address`,
            params: {
              houseno: this.country,
              road: this.address,
              zip: this.zib
            }
          })
          // console.log(groupCheck.data)
          if (groupCheck.data.response === 'failed') {
            this.step++
          } else {
            this.dialogBox = true
          }

          // this.step++
        }
        //  this.step++
      } else if (this.step == 3) {

        this.v$.studentId.$validate();
        this.v$.university.$validate();
        this.v$.ArrivedDate.$validate();
        this.v$.startDate.$validate();
        this.v$.EndDate.$validate();


        if (this.v$.$errors.length == 0) {
          this.step++
        }


      }
    },
    noGroup() {
      this.grp_err_text = "Please Add Another Address";
      this.dialogBox = false;
      this.v$.country.$reset();
      this.v$.address.$reset();
      this.v$.zib.$reset();
      this.country = '';
      this.zib = '';
      this.address = '';
    },
    async submitMemeber() {
      this.v$.deitryRequirment.$validate();
      this.v$.eggs.$validate();
      this.v$.collectionDay.$validate();
      if (this.v$.$errors.length == 0) {
        if (!this.select) {
          this.snackColor = 'error';
          this.mssg = 'Please Agree to Terms and Conditions'
          this.snackbar = true;

        } else {
          const payload = {
            statusId: 1,
            title: this.titles,
            firstname: this.firstName,
            lastname: this.secondName,
            phone: this.phone,
            email: this.email,
            membersroleId: this.type,
            dependentname: this.DependentName,
            noofkids: this.NoKids,
            area: 0,
            country: this.formAddress.county,
            road: this.address,
            borough: this.borough,
            address: [this.formAddress.line_1, this.formAddress.line_2, this.formAddress.line_3 , this.formAddress.district, this.formAddress.county],
            houseno: this.country,
            zip: this.zib,
            studentid: this.studentId,
            universityId: this.university,
            arrivedin: this.ArrivedDate,
            coursestart: this.startDate,
            courseend: this.EndDate,
            diettype: 1,
            dietTypeId: this.deitryRequirment,
            eggs: this.eggs,
            preferredcollectionday: this.collectionDay,
            dependentname: this.dependentName,
            dependentphone: this.dependentPhone,
            dependentemail: this.dependentemail,
            membertype: this.memberType

            // expected: this.ExpectedDate
          }
          if (this.type > 3) {
            payload.membersroleId = 2
          }

          if (this.NoKids && !isNaN(this.NoKids)) {
            payload.children = this.child
            payload.membersroleId = 3
            if (this.type != 1) {
              payload.membersroleId = 2
            }
          }
          if (this.NoKids == 'Expected') {
            payload.noofkids = null;
            payload.expected = this.ExpectedDate;
            payload.membersroleId = 3
          }
          // this.$emit('add_memeber', payload)
          // console.log(payload)
          
          await this.ADD_MEMBER(payload).then(() => {
            this.titles = ''
            this.firstName = ''
            this.secondName = ''
            this.phone = ''
            this.email = ''
            this.type = ''
            this.DependentName = ''
            this.NoKids = null
            this.heveDependent = null
            this.area = ''
            this.country = ''
            this.borough = ''
            this.address = ''
            this.zib = ''
            this.studentId = ''
            this.university = ''
            this.ArrivedDate = ''
            this.startDate = ''
            this.EndDate = ''
            this.deitryRequirment = ''
            this.eggs = ''
            this.collectionDay = ''
            this.dependentName = ''
            this.dependentPhone = ''
            this.dependentemail = ''
            this.select = ''
            this.v$.$reset();
            this.step = 1
            this.$router.push({ name: 'registration-success' })
          }).catch(err => {
            console.log(err)
            this.snackColor = 'error';
            this.mssg = err;
            this.snackbar = true;
          })

        }

      }
    },

    goBack() {
      if (this.step == 5) {
        this.step = 1

      }
      else if (this.step == 4 && this.type != 1) {
        this.step = 2
      }
      else {
        this.step--
      }
    },
    set_role() {
      this.Role = this.memberRole;
    },
    set_borough() {

    },
    submitMemebers() {
    }
  },

  beforeMount() {
    // this.GET_MEMBER_LIST();
    // this.GET_MEMBER_ROLE();
    // this.GET_BORUGH_LIST(query);
    // this.GET_UNIVERSITY_LIST(query);




    // boroughList
    // universityLis

  },
  // beforeCreate(){
  //   document.body.className
  // }
}
</script>
<style>
@import url('https://fonts.googleapis.com/css2?family=Jost:wght@500&family=Montserrat:wght@400;500;600;800&family=Sora:wght@400;500&display=swap');

.v-main {
  background:
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.22), transparent 34%),
    linear-gradient(135deg, #5c837a 0%, #719991 48%, #dce7e2 100%);
  min-height: 100vh;
}

@media only screen and (max-width: 700px) {
  .v-main {
    background: #f4f8f7;
  }
}
</style>

<style scoped>
.signup-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  font-family: Montserrat, sans-serif;
}

.signup-shell {
  overflow: hidden;
  background: #ffffff;
}

.signup-hero {
  background: linear-gradient(180deg, #20443f 0%, #2f6f65 100%);
  color: #ffffff;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.signup-hero__logo {
  padding-top: 8px;
}

.signup-hero__eyebrow {
  letter-spacing: 0.14em;
  opacity: 0.8;
}

.signup-hero__title {
  margin: 0 0 12px;
  font-size: 2rem;
  line-height: 1.1;
  font-weight: 700;
}

.signup-hero__subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
}

.signup-hero__steps {
  display: grid;
  gap: 12px;
}

.step-pill {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.09);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.2s ease;
}

.step-pill--active {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.24);
  transform: translateX(4px);
}

.step-pill--done {
  background: rgba(196, 255, 226, 0.16);
}

.step-pill__number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  font-weight: 700;
  flex-shrink: 0;
}

.step-pill__title {
  font-weight: 600;
  margin-bottom: 4px;
}

.step-pill__subtitle {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.4;
}

.signup-hero__note {
  margin-top: auto;
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
}

.signup-hero__note-title {
  font-weight: 700;
  margin-bottom: 8px;
}

.signup-form-column {
  background: #fcfdfd;
}

.signup-form {
  padding: 32px;
}

.signup-form__header {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 24px;
}

.signup-form__title {
  margin: 10px 0 8px;
  font-size: 1.85rem;
  color: #172725;
  font-weight: 700;
}

.signup-form__subtitle {
  margin: 0;
  color: #5f6f6c;
  line-height: 1.6;
}

.signup-form__progress {
  min-width: 150px;
  max-width: 200px;
  width: 100%;
  color: #2f6f65;
  font-weight: 700;
}

.signup-form__progress span {
  display: inline-block;
  margin-bottom: 8px;
}

.step-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: #e8f1ef;
  color: #2f6f65;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.section-block,
.section-card {
  padding: 24px;
  border: 1px solid #e5ece9;
  border-radius: 24px;
  background: #ffffff;
}

.section-card {
  box-shadow: 0 16px 32px rgba(32, 68, 63, 0.04);
}

.section-block__header {
  margin-bottom: 20px;
}

.section-block__header h3 {
  margin: 0 0 6px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #172725;
}

.section-block__header p {
  margin: 0;
  color: #687a76;
  line-height: 1.6;
}

.section-divider {
  text-align: center;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6a7c78;
}

.terms-card {
  padding: 8px 14px;
}

.terms-card :deep(.v-selection-control) {
  align-items: flex-start;
  width: 100%;
}

.terms-card :deep(.v-selection-control__wrapper) {
  margin-top: 2px;
}

.terms-card :deep(.v-selection-control__input) {
  flex-shrink: 0;
}

.terms-card :deep(.v-selection-control__label) {
  min-width: 0;
  flex: 1 1 auto;
}

.terms-card__label {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.6;
}

.child-card {
  border-color: #dce6e2;
  background: #ffffff;
}

.child-card__title {
  margin-bottom: 14px;
  font-weight: 700;
  color: #20443f;
}

.signup-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e6ece9;
}

.signup-actions__spacer {
  flex: 1;
}

:deep(.v-field) {
  box-shadow: none;
}

:deep(.v-field--variant-outlined .v-field__outline) {
  color: #565a59;
}

:deep(.v-label.v-field-label) {
  color: #6a7c78;
}

@media only screen and (max-width: 960px) {
  .signup-form__header {
    flex-direction: column;
  }

  .signup-form__progress {
    max-width: none;
  }
}

@media only screen and (max-width: 700px) {
  .signup-page {
    padding: 12px;
  }

  .signup-hero {
    display: none;
  }

  .signup-form {
    padding: 22px;
  }

  .signup-form__title {
    font-size: 1.55rem;
  }

  .section-block,
  .section-card {
    padding: 18px;
    border-radius: 20px;
  }

  .signup-actions {
    flex-wrap: wrap;
  }

  .signup-actions__spacer {
    display: none;
  }

  .signup-actions :deep(.v-btn) {
    width: 100%;
  }
}
</style>
